import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Bell, CheckCircle, XCircle, DollarSign, Calendar, FileWarning,
  AlertTriangle, Filter, CheckCheck, Trash2, Inbox, BellRing, Clock,
  Users, Shield, ShoppingCart,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const NOTIF_ICONS = {
  leave_approved: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
  leave_rejected: { icon: XCircle, color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
  leave_request: { icon: Calendar, color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
  salary_paid: { icon: DollarSign, color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
  document_expiry: { icon: FileWarning, color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
  budget_alert: { icon: AlertTriangle, color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
  system: { icon: Shield, color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
  sale: { icon: ShoppingCart, color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
  hr: { icon: Users, color: 'text-teal-600', bg: 'bg-teal-50', ring: 'ring-teal-100' },
};

const FILTER_TABS = [
  { key: 'all', label: 'All', icon: Bell },
  { key: 'unread', label: 'Unread', icon: BellRing },
  { key: 'budget_alert', label: 'Budget', icon: AlertTriangle },
  { key: 'hr', label: 'HR', icon: Users },
];

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return format(new Date(dateStr), 'MMM dd');
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
      await api.post('/notifications/mark-read').catch(() => {});
    } catch { toast.error('Failed to fetch notifications'); }
    finally { setLoading(false); }
  };

  const dismissNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification dismissed');
    } catch {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear-all').catch(() => {});
      setNotifications([]);
      toast.success('All notifications cleared');
    } catch {
      setNotifications([]);
    }
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'budget_alert') return n.type === 'budget_alert';
    if (filter === 'hr') return ['leave_approved', 'leave_rejected', 'leave_request', 'salary_paid'].includes(n.type);
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;
  const budgetCount = notifications.filter(n => n.type === 'budget_alert').length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-border border-t-stone-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <Bell size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="notifications-title">Notifications</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                {budgetCount > 0 && ` · ${budgetCount} budget alerts`}
              </p>
            </div>
          </div>
          {notifications.length > 0 && (
            <Button variant="outline" size="sm" className="rounded-xl text-xs text-muted-foreground" onClick={clearAll} data-testid="clear-all-btn">
              <Trash2 size={14} className="mr-1.5" /> Clear All
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1.5 p-1 bg-muted rounded-xl w-fit" data-testid="filter-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all ${
                filter === tab.key
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-muted-foreground'
              }`}
              data-testid={`filter-${tab.key}`}
            >
              <tab.icon size={13} />
              {tab.label}
              {tab.key === 'unread' && unreadCount > 0 && (
                <Badge className="bg-info/20 text-info border-info/30 text-[9px] px-1.5 py-0 h-4">{unreadCount}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* Notification List */}
        {filtered.length === 0 ? (
          <Card className="border-border">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                <Inbox size={28} className="text-muted-foreground" />
              </div>
              <h3 className="font-outfit font-semibold text-muted-foreground text-base">No notifications</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {filter === 'unread' ? 'All notifications have been read' :
                 filter === 'budget_alert' ? 'No budget alerts yet — configure rules in Budget vs Actual' :
                 'You\'re all caught up'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((n, i) => {
              const config = NOTIF_ICONS[n.type] || { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' };
              const Icon = config.icon;
              const severity = n.data?.severity;
              return (
                <div
                  key={n.id}
                  className={`group flex items-start gap-3.5 p-4 rounded-xl border transition-all duration-200 hover:shadow-sm ${
                    !n.read
                      ? 'bg-info/10 border-info/30'
                      : 'bg-white border-border hover:border-border'
                  }`}
                  style={{ animationDelay: `${i * 30}ms` }}
                  data-testid="notification-item"
                >
                  <div className={`w-9 h-9 rounded-xl ${config.bg} ${config.color} flex items-center justify-center shrink-0 ring-1 ${config.ring}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-foreground leading-tight">{n.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{n.message}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-info shrink-0" />
                        )}
                        {severity && (
                          <Badge className={`text-[9px] uppercase ${
                            severity === 'exceeded' ? 'bg-error/20 text-error border-error/30' :
                            severity === 'critical' ? 'bg-warning/20 text-warning border-warning/30' :
                            'bg-warning/20 text-warning border-warning/30'
                          }`}>{severity}</Badge>
                        )}
                        <button
                          onClick={() => dismissNotification(n.id)}
                          className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-muted transition-all"
                          data-testid="dismiss-notification"
                        >
                          <XCircle size={14} className="text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock size={10} className="text-muted-foreground" />
                      <span className="text-[10px] text-muted-foreground">{getTimeAgo(n.created_at)}</span>
                      <span className="text-[10px] text-muted-foreground">&middot;</span>
                      <span className="text-[10px] text-muted-foreground">{format(new Date(n.created_at), 'MMM dd, hh:mm a')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
